/**
 * "Is this request coming from the phone app rather than a browser?"
 *
 * The GrowthSync app is a thin Capacitor shell that loads growth-sync.com over the
 * network (capacitor.config.ts -> server.url), so the app and the website are the
 * same pages served by the same server. Anywhere the two should differ, this is the
 * switch.
 *
 * Detection is deliberately layered, because there is no single signal available on
 * a top-level document navigation:
 *
 * 1. User-Agent. capacitor.config.ts appends " Capacitor" on BOTH platforms. Android
 *    has done this for a long time; iOS only from the build shipped after 2026-08-23,
 *    so an iPhone running an older binary will NOT match here.
 * 2. The `gs_shell` cookie, written by NativeShellCookie on the client the first time
 *    the app runs (the client can ask Capacitor directly, the server cannot). This is
 *    what covers older iOS binaries from their second launch onwards.
 *
 * Neither signal is a security boundary and neither is treated as one: the worst a
 * forged value can do is show a browser the app's welcome screen instead of the
 * marketing page. Auth is unaffected.
 *
 * Deliberately NOT used: "iOS WKWebView has no Safari/ token in its User-Agent".
 * That is true of the Capacitor shell, but it is equally true of the in-app browsers
 * in Instagram, Facebook and LinkedIn, which is exactly where marketing links get
 * opened. Matching on it would hide the landing page from the people it is for.
 */

const CAPACITOR_UA = /Capacitor|Cap\/\d/i;

export const NATIVE_SHELL_COOKIE = "gs_shell";

export type NativeShellPlatform = "ios" | "android";

function readUserAgent(headers: Headers): string {
  return headers.get("user-agent") ?? "";
}

/** Server side: does this request come from the iOS or Android app shell? */
export function isNativeShellRequest(request: { headers: Headers; cookies?: { get(name: string): { value: string } | undefined } }): boolean {
  const ua = readUserAgent(request.headers);
  if (CAPACITOR_UA.test(ua)) return true;

  const cookie = request.cookies?.get(NATIVE_SHELL_COOKIE)?.value;
  return cookie === "ios" || cookie === "android";
}

/**
 * The same question, asked from a server component rather than from middleware.
 *
 * Layouts and pages do not receive the NextRequest, so they cannot call
 * isNativeShellRequest directly. This reads the same two signals out of next/headers
 * and next/cookies, which keeps one definition of "is this the app" rather than two
 * that drift.
 *
 * Calling it opts the route into dynamic rendering, which every page that uses it is
 * already doing.
 */
export async function isNativeShellFromHeaders(): Promise<boolean> {
  const { headers, cookies } = await import("next/headers");
  const [headerList, cookieStore] = await Promise.all([headers(), cookies()]);
  return isNativeShellRequest({
    headers: headerList as unknown as Headers,
    cookies: { get: (name: string) => cookieStore.get(name) },
  });
}
